<script setup>
import { ref, reactive, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  audienceList: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['imported']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const fileInputRef = ref(null);

const IGNORE = '__ignore__';
const POLL_INTERVAL_MS = 2000;
const MAPPABLE_FIELDS = ['name', 'phone_number', 'email', 'identifier'];
// Matches the server-side guard: refuse the upload in the browser instead of
// pushing 60 MB over the wire only to be rejected.
const MAX_FILE_BYTES = 25 * 1024 * 1024;

const state = reactive({
  step: 'file', // file | mapping | running | done
  file: null,
  headers: [],
  rows: [],
  extraHeaders: [],
  mapping: {},
  isParsing: false,
  isSubmitting: false,
  importRecord: null,
  // Applied to numbers that arrive without a country code. Brazilian exports
  // usually do, and the fallback was invisible to the operator.
  defaultCountryCode: '55',
});

let pollTimer = null;

const stopPolling = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
};

onBeforeUnmount(stopPolling);

const resetState = () => {
  stopPolling();
  Object.assign(state, {
    step: 'file',
    file: null,
    headers: [],
    rows: [],
    extraHeaders: [],
    mapping: {},
    isParsing: false,
    isSubmitting: false,
    importRecord: null,
    defaultCountryCode: '55',
  });
  if (fileInputRef.value) fileInputRef.value.value = '';
};

const headerOptions = computed(() => [
  { value: IGNORE, label: t('CAMPAIGN.AUDIENCE.IMPORT.IGNORE_COLUMN') },
  ...state.headers.map(header => ({ value: header, label: header })),
]);

// Columns not bound to a canonical field still get imported, as free-form
// attributes — surfacing them here keeps that behaviour explicit.
const unmappedHeaders = computed(() => {
  const bound = Object.values(state.mapping).filter(
    value => value && value !== IGNORE
  );
  return state.headers.filter(header => !bound.includes(header));
});

const canSubmit = computed(() => {
  const phone = state.mapping.phone_number;
  const email = state.mapping.email;
  return (phone && phone !== IGNORE) || (email && email !== IGNORE);
});

const handleFileChange = async event => {
  const [file] = event.target.files ?? [];
  if (!file) return;

  if (file.size > MAX_FILE_BYTES) {
    useAlert(
      t('CAMPAIGN.AUDIENCE.IMPORT.API.FILE_TOO_LARGE', {
        limit: Math.round(MAX_FILE_BYTES / (1024 * 1024)),
      })
    );
    resetState();
    return;
  }

  state.file = file;
  state.isParsing = true;
  try {
    const preview = await store.dispatch('audienceLists/previewFile', file);
    state.headers = preview.headers ?? [];
    state.rows = preview.rows ?? [];
    state.extraHeaders = preview.extra_headers ?? [];
    state.mapping = MAPPABLE_FIELDS.reduce((acc, field) => {
      acc[field] = preview.suggested_mapping?.[field] ?? IGNORE;
      return acc;
    }, {});
    state.step = 'mapping';
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.AUDIENCE.IMPORT.API.PARSE_ERROR')
    );
    resetState();
  } finally {
    state.isParsing = false;
  }
};

const pollImport = async () => {
  try {
    const record = await store.dispatch('audienceLists/pollImport', {
      id: props.audienceList.id,
      importId: state.importRecord.id,
    });
    state.importRecord = record;

    if (['completed', 'failed'].includes(record.status)) {
      state.step = 'done';
      emit('imported', record);
      return;
    }
  } catch (error) {
    // A transient polling failure must not kill the import — it keeps running
    // in Sidekiq; retry on the next tick.
  }
  pollTimer = setTimeout(pollImport, POLL_INTERVAL_MS);
};

const startImport = async () => {
  state.isSubmitting = true;
  try {
    const columnMapping = Object.fromEntries(
      Object.entries(state.mapping).filter(
        ([, value]) => value && value !== IGNORE
      )
    );
    state.importRecord = await store.dispatch('audienceLists/createImport', {
      id: props.audienceList.id,
      file: state.file,
      columnMapping,
      defaultCountryCode: state.defaultCountryCode,
    });
    state.step = 'running';
    pollTimer = setTimeout(pollImport, POLL_INTERVAL_MS);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.AUDIENCE.IMPORT.API.ERROR_MESSAGE')
    );
  } finally {
    state.isSubmitting = false;
  }
};

const handleConfirm = () => {
  if (state.step === 'mapping') {
    startImport();
    return;
  }
  if (state.step === 'done') {
    dialogRef.value?.close();
  }
};

const confirmButtonLabel = computed(() => {
  if (state.step === 'done') return t('CAMPAIGN.AUDIENCE.IMPORT.BUTTONS.CLOSE');
  return t('CAMPAIGN.AUDIENCE.IMPORT.BUTTONS.IMPORT');
});

// A dialog closed mid-import leaves the card counters stale, because polling is
// what refreshes them. Ask for the record once on the way out.
const handleClose = () => {
  const wasRunning = state.step === 'running';
  resetState();
  if (wasRunning && props.audienceList?.id) {
    store.dispatch('audienceLists/show', props.audienceList.id);
  }
};

const open = () => {
  resetState();
  dialogRef.value?.open();
};

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('CAMPAIGN.AUDIENCE.IMPORT.TITLE', { name: audienceList?.name })"
    :description="t('CAMPAIGN.AUDIENCE.IMPORT.DESCRIPTION')"
    :confirm-button-label="confirmButtonLabel"
    :show-confirm-button="state.step === 'mapping' || state.step === 'done'"
    :disable-confirm-button="
      state.step === 'mapping' && (!canSubmit || state.isSubmitting)
    "
    :is-loading="state.isSubmitting"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <!-- Step 1: file -->
    <div v-if="state.step === 'file'" class="flex flex-col gap-3">
      <label
        class="flex flex-col items-center justify-center gap-2 px-4 py-10 border border-dashed rounded-lg cursor-pointer border-n-weak hover:bg-n-alpha-1"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.AUDIENCE.IMPORT.FILE.LABEL') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.AUDIENCE.IMPORT.FILE.HINT') }}
        </span>
        <!-- sr-only, not `hidden`: display:none drops the input out of the tab
             order, and the wrapping <label> is not focusable either, so step 1
             of the wizard was unreachable without a mouse. -->
        <input
          ref="fileInputRef"
          type="file"
          class="sr-only"
          accept=".csv,.tsv,.txt,text/csv,text/plain"
          @change="handleFileChange"
        />
      </label>
      <div
        v-if="state.isParsing"
        class="flex items-center justify-center gap-2 text-sm text-n-slate-11"
      >
        <Spinner />
        {{ t('CAMPAIGN.AUDIENCE.IMPORT.FILE.PARSING') }}
      </div>
    </div>

    <!-- Step 2: mapping + preview -->
    <div v-else-if="state.step === 'mapping'" class="flex flex-col gap-5">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-xs truncate text-n-slate-11">
          {{ state.file?.name }}
        </span>
        <Button
          type="button"
          variant="link"
          color="slate"
          size="xs"
          icon="i-lucide-arrow-left"
          :label="t('CAMPAIGN.AUDIENCE.IMPORT.BUTTONS.CHANGE_FILE')"
          @click="resetState"
        />
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div
          v-for="field in MAPPABLE_FIELDS"
          :key="field"
          class="flex flex-col gap-1"
        >
          <label
            :for="`audience-mapping-${field}`"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t(`CAMPAIGN.AUDIENCE.IMPORT.FIELDS.${field.toUpperCase()}`) }}
          </label>
          <ComboBox
            :id="`audience-mapping-${field}`"
            v-model="state.mapping[field]"
            :options="headerOptions"
            :aria-label="
              t(`CAMPAIGN.AUDIENCE.IMPORT.FIELDS.${field.toUpperCase()}`)
            "
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1 sm:max-w-xs">
        <label
          for="audience-default-country-code"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.AUDIENCE.IMPORT.COUNTRY_CODE.LABEL') }}
        </label>
        <Input
          id="audience-default-country-code"
          v-model="state.defaultCountryCode"
          :placeholder="t('CAMPAIGN.AUDIENCE.IMPORT.COUNTRY_CODE.PLACEHOLDER')"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.AUDIENCE.IMPORT.COUNTRY_CODE.HINT') }}
        </span>
      </div>

      <p v-if="!canSubmit" class="text-sm text-n-ruby-11">
        {{ t('CAMPAIGN.AUDIENCE.IMPORT.REQUIRE_PHONE_OR_EMAIL') }}
      </p>

      <div
        v-if="unmappedHeaders.length"
        class="flex flex-wrap items-center gap-1.5"
      >
        <span class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.AUDIENCE.IMPORT.EXTRA_COLUMNS') }}
        </span>
        <span
          v-for="header in unmappedHeaders"
          :key="header"
          class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
        >
          {{ header }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.AUDIENCE.IMPORT.PREVIEW_TITLE') }}
        </span>
        <!-- Scrolls inside its own box: an imported base can have 30 columns
             and the dialog must never scroll sideways as a whole. -->
        <div class="overflow-x-auto border rounded-lg border-n-weak">
          <table class="w-full text-xs text-left whitespace-nowrap">
            <thead class="bg-n-alpha-1 text-n-slate-11">
              <tr>
                <th
                  v-for="header in state.headers"
                  :key="header"
                  class="px-3 py-2 font-medium whitespace-nowrap"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in state.rows.slice(0, 5)"
                :key="index"
                class="border-t border-n-weak text-n-slate-12"
              >
                <td
                  v-for="header in state.headers"
                  :key="header"
                  class="px-3 py-2 whitespace-nowrap"
                >
                  {{ row[header] }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Step 3: running -->
    <div
      v-else-if="state.step === 'running'"
      class="flex flex-col items-center gap-3 py-8"
    >
      <Spinner />
      <span class="text-sm text-n-slate-11">
        {{
          t('CAMPAIGN.AUDIENCE.IMPORT.RUNNING', {
            processed: state.importRecord?.processed_rows ?? 0,
          })
        }}
      </span>
    </div>

    <!-- Step 4: result -->
    <div v-else class="flex flex-col gap-3">
      <p
        v-if="state.importRecord?.status === 'failed'"
        class="text-sm text-n-ruby-11"
      >
        {{
          state.importRecord?.error_message ||
          t('CAMPAIGN.AUDIENCE.IMPORT.API.ERROR_MESSAGE')
        }}
      </p>
      <div v-else class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div
          v-for="metric in [
            {
              key: 'imported_count',
              label: t('CAMPAIGN.AUDIENCE.IMPORT.RESULT.IMPORTED'),
            },
            {
              key: 'updated_count',
              label: t('CAMPAIGN.AUDIENCE.IMPORT.RESULT.UPDATED'),
            },
            {
              key: 'skipped_count',
              label: t('CAMPAIGN.AUDIENCE.IMPORT.RESULT.SKIPPED'),
            },
            {
              key: 'failed_count',
              label: t('CAMPAIGN.AUDIENCE.IMPORT.RESULT.FAILED'),
            },
          ]"
          :key="metric.key"
          class="flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-n-alpha-1"
        >
          <span class="text-lg font-medium text-n-slate-12">
            {{ state.importRecord?.[metric.key] ?? 0 }}
          </span>
          <span class="text-xs text-n-slate-11">{{ metric.label }}</span>
        </div>
      </div>

      <div
        v-if="state.importRecord?.row_errors?.length"
        class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1 max-h-40 overflow-y-auto"
      >
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CAMPAIGN.AUDIENCE.IMPORT.RESULT.ROW_ERRORS') }}
        </span>
        <span
          v-for="rowError in state.importRecord.row_errors"
          :key="rowError.row"
          class="text-xs text-n-slate-11"
        >
          {{
            t('CAMPAIGN.AUDIENCE.IMPORT.RESULT.ROW_ERROR_ITEM', {
              row: rowError.row,
              reason: rowError.reason,
            })
          }}
        </span>
      </div>
    </div>
  </Dialog>
</template>
